import { OrderRulesFilterParams, OrderRulesFilterRequest } from './types';

// ─── Helper: build POST body from filter params ───────────────────────────────

const splitCsv = (value?: string): string[] => {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

const splitCsvStrings = (value?: string): string[] => splitCsv(value).map((item) => item.trim()).filter(Boolean);

const splitCsvNumbers = (value?: string): number[] =>
  splitCsv(value)
    .map((s) => Number(s.replace(/^P/i, '').replace(/^W/i, '')))
    .filter((n) => Number.isFinite(n));

/** Unwraps the `{ data: ... }` envelope some endpoints use, leaving already-flat payloads untouched. */
export const unwrapApiPayload = <T>(payload: any): T => {
  if (!payload || typeof payload !== 'object') return payload;

  const wrappedFields = ['tableRows', 'grandTotal', 'kpiCards', 'skuRecommendations', 'openRecommendations', 'dataPoints', 'rows'];
  const hasDirectData = wrappedFields.some((field) => Object.prototype.hasOwnProperty.call(payload, field));

  if (hasDirectData) return payload as T;
  return (payload.data ?? payload) as T;
};

/**
 * Converts the UI's flat filter params + an explicit locationIds array into the
 * backend `OrderRulesFilterRequest` POST body.
 *
 * `locationIds` carries Loc_SAP_ID values — the UI selects locations by SAP ID.
 */
export const buildOrderRulesRequest = (
  params: OrderRulesFilterParams,
  locationIds: string[],
  pagination?: { page: number; size: number; sortBy?: string; sortDirection?: 'asc' | 'desc' },
  options?: { includeCustomerMaterialFilters?: boolean; granularity?: string[] },
): OrderRulesFilterRequest => {
  // Max Potential Execution does not support Customer/Material filters — every other endpoint does.
  const includeCustomerMaterialFilters = options?.includeCustomerMaterialFilters ?? true;
  const body: OrderRulesFilterRequest = {};

  if (options?.granularity && options.granularity.length > 0) body.granularity = options.granularity;

  const regions = splitCsvStrings(params.region);
  const markets = splitCsvStrings(params.market);
  const years = splitCsvNumbers(params.year);
  const periods = splitCsvNumbers(params.period);
  const weeks = splitCsvNumbers(params.week);

  if (locationIds.length > 0) body.locationIds = locationIds;
  if (regions.length > 0) body.regions = regions;
  if (markets.length > 0) body.markets = markets;

  if (includeCustomerMaterialFilters) {
    const keyAccounts = splitCsvStrings(params.keyAccount);
    const customerIds = splitCsvStrings(params.customerBp);
    const customerName = splitCsvStrings(params.customerName);
    const materialId = splitCsvStrings(params.materialNumber);
    const materialName = splitCsvStrings(params.materialName);
    const packageName = splitCsvStrings(params.packageName);

    if (keyAccounts.length > 0) body.keyAccounts = keyAccounts;
    if (customerIds.length > 0) body.customerIds = customerIds;
    if (customerName.length > 0) body.customerName = customerName;
    if (materialId.length > 0) body.materialId = materialId;
    if (materialName.length > 0) body.materialName = materialName;
    if (packageName.length > 0) body.packageName = packageName;
  }

  if (years.length > 0) body.years = years;
  if (periods.length > 0) body.periods = periods;
  if (weeks.length > 0) body.weeks = weeks;

  if (params.startDate) body.startDate = params.startDate;
  if (params.endDate) body.endDate = params.endDate;
  if (params.timeGrain) body.timeGrain = params.timeGrain;
  if (params.geographicDrillLevel != null) body.geographicDrillLevel = params.geographicDrillLevel;
  if (params.sortBy) body.sortBy = params.sortBy;
  if (params.sortOrder) body.sortOrder = params.sortOrder;
  if (params.pageNumber != null) body.pageNumber = params.pageNumber;
  if (params.pageSize != null) body.pageSize = params.pageSize;

  if (pagination) {
    // Backend expects 1-based pageNumber/pageSize (matching the one-pager convention), not 0-based page/size.
    body.pageNumber = pagination.page + 1;
    body.pageSize = pagination.size;
    if (pagination.sortBy) body.sortBy = pagination.sortBy;
    if (pagination.sortDirection) body.sortDirection = pagination.sortDirection;
  }

  return body;
};
